import Card from "@/components/card";
import CardSkeleton from "@/components/skeletons/card-skeleton";
import { DottedBorderBox } from "@/components/dotted-border";
import TopBar from "@/components/topbar";
import { Button } from "@/components/ui/button";
import {
  useGetCards,
  useGetCardTransactions,
  useGetPendingCards,
} from "@/hooks/use-queries";
import { useSheetStore } from "@/zustand/sheetStore";
import { PlusCircle } from "lucide-react";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { DataTable } from "@/components/data-table";
import { cardColumns } from "../_misc/cardColumns";
import TransactionList from "../_misc/TransactionList";

const Cards = () => {
  const { data, isLoading } = useGetCards();
  const { data: pendingCards } = useGetPendingCards();
  const cards = data as { data: Card[] };
  const { openSheet } = useSheetStore();
  const { data: cardTransactions, isLoading: isCardLoading } =
    useGetCardTransactions({
      page: 0,
      limit: 150,
    });

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  return (
    <div>
      {/* header */}
      <TopBar title={`My Cards`} />

      <div className="relative px-4">
        <img
          src="/images/bg-logo.png"
          alt=""
          className="fixed right-1/2 bottom-[calc(50%-100px)] z-0 translate-x-1/2 translate-y-1/2 lg:right-2/5"
        />

        <div
          className={`relative mt-6 grid w-full grid-cols-1 gap-4 lg:grid-cols-9`}
        >
          <div
            className="relative col-span-1 row-start-2 h-full min-h-60 overflow-hidden rounded-2xl bg-[linear-gradient(180deg,rgba(217,217,217,0.276)_0%,rgba(217,217,217,0.069)_100%)] p-4 pb-0 lg:col-span-4 lg:row-start-auto"
            style={{
              backdropFilter: "blur(50px)",
              boxShadow: "0px 4px 17px -1px #BDE9FB33",
            }}
          >
            <img
              src="/images/grain-texture.jpg"
              alt=""
              className="absolute inset-0 z-0 h-full w-full object-cover opacity-2"
            />

            {isLoading ? (
              <div className="mt-6 flex w-full items-center justify-center">
                <CardSkeleton />
              </div>
            ) : cards?.data.length === 0 ? (
              <div className="flex h-full items-center justify-center text-white">
                <div className="flex flex-col text-center">
                  <h2 className="text-2xl font-bold">No Cards</h2>
                  <p className="max-w-50 text-sm leading-4 font-normal">
                    Instantly create a card to start making transactions
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-4 flex items-center justify-between text-white">
                  <span>Select Card</span>
                </div>
                <div
                  className={`thin-scrollbar relative flex w-full gap-4 overflow-x-auto pb-4 ${cards?.data.length === 1 ? "justify-center" : ""}`}
                >
                  {cards?.data.map((card, index) => (
                    <Card key={index} card={card} />
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="flex flex-row gap-2.5 text-white *:grow lg:col-span-3 lg:flex-col">
            <div
              className="rounded-xl p-4 backdrop-blur-sm"
              style={{
                background:
                  "linear-gradient(129.49deg, rgba(41, 41, 52, 0.9) 3.6%, rgba(72, 72, 93, 0.9) 100%)",
              }}
            >
              <div className="flex items-center justify-between text-[10px]">
                <img className="size-6" src="/icons/gradient-card.svg" alt="" />
                <div className="flex gap-2">
                  <div>
                    <div className="text-[10px] text-[#C9D6FF] lg:text-[10px]">
                      Frozen
                    </div>
                    <div className="text-xs">
                      {cards?.data.filter((v) => v.status === "Frozen")?.length}
                    </div>
                  </div>
                  <div>
                    <div className="text-[#FF6E7A]">Expired</div>
                    <div className="text-xs">0</div>
                  </div>
                </div>
              </div>

              <div className="mt-6 mb-2">
                <div className="text-xs lg:text-sm">Total card</div>
                <div className="text-xl font-semibold lg:text-2xl">
                  {cards?.data.length} Cards
                </div>
              </div>
            </div>
            <div className="flex w-1/2 flex-col space-y-2.5 lg:w-full">
              <div className="font-dm-sans relative min-w-1/2 space-x-1.5 overflow-hidden rounded bg-[linear-gradient(104.01deg,#1F2021_2.88%,#3A3B40_99.58%)] p-2 px-4 *:grow lg:hidden lg:w-auto lg:rounded-2xl lg:p-4">
                <DottedBorderBox strokeColor={"#EDD6FF"} strokeWidth={0.5} />
                <div
                  className="text-primary-500 absolute top-0 -right-2 px-2.5 py-2 text-[10px] font-light uppercase"
                  style={{
                    background:
                      "linear-gradient(129.49deg, rgba(41, 41, 52, 0.9) 3.6%, rgba(72, 72, 93, 0.9) 100%)",
                  }}
                >
                  Coming soon
                </div>
                <div className="opacity-30">
                  <img
                    className="size-4 lg:size-auto"
                    src="/icons/report.svg"
                    alt="Report icon"
                  />
                  <p className="mt-1 text-xs font-medium text-white lg:mt-2 lg:text-sm">
                    Card Statement
                  </p>
                  <p className="text-[8px] font-light text-white lg:mt-1 lg:text-xs">
                    View and track transactions from your card
                  </p>
                </div>
              </div>
              <div className="font-dm-sans relative box-border flex justify-between gap-y-4 rounded-2xl bg-[linear-gradient(104.01deg,#1F2021_2.88%,#3A3B40_99.58%)] p-1.5 text-white lg:h-full lg:w-full lg:p-3">
                <DottedBorderBox strokeColor={"#7A766B"} strokeWidth={0.5} />
                <Button
                  onClick={() => openSheet("createCard", 2, {}, false)}
                  className="text-primary-500 hover:text-primary-500 bg-primary-100 hover:bg-primary-100-hover h-10 w-full py-2 font-semibold lg:h-12"
                >
                  <PlusCircle strokeWidth={2} className="size-5" />
                  <span>Buy Card</span>
                </Button>
              </div>
            </div>
          </div>
          <div className="hidden flex-col gap-2.5 text-white lg:col-span-2 lg:flex">
            <div className="font-dm-sans relative h-29 min-w-1/2 space-x-1.5 overflow-hidden rounded-2xl bg-[linear-gradient(129.49deg,rgba(41,41,52,0.9)3.6%,rgba(72,72,93,0.9)100%)] p-4 *:grow">
              <DottedBorderBox strokeColor={"#EDD6FF"} strokeWidth={0.5} />
              <div
                className="text-primary-500 absolute top-0 -right-2 px-2.5 py-2 text-[10px] font-light uppercase"
                style={{
                  background:
                    " linear-gradient(128.62deg, #9191CB 11.02%, #EDD6FF 93.11%)",
                }}
              >
                Coming soon
              </div>
              <div className="opacity-30">
                <img src="/icons/report.svg" alt="Report icon" />
                <p className="mt-2 text-sm font-medium text-white">
                  Card Statement
                </p>
                <p className="mt-1 text-xs font-light text-white">
                  View and track transactions from your card
                </p>
              </div>
            </div>
            <div className="font-dm-sans relative h-29 min-w-1/2 space-x-1.5 overflow-hidden rounded-2xl bg-[linear-gradient(129.49deg,rgba(41,41,52,0.9)3.6%,rgba(72,72,93,0.9)100%)] p-4 *:grow">
              <DottedBorderBox strokeColor={"#EDD6FF"} strokeWidth={0.5} />

              <div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <img src="/icons/pending-card.svg" alt="Report icon" />
                    <p className="text-sm font-medium text-[#A8CAFF]">
                      Pending card
                    </p>
                  </div>
                  <span className="text-sm font-extrabold">
                    {" "}
                    {pendingCards?.data?.length}
                  </span>
                </div>
                <p className="mt-2.5 text-xs font-light text-white">
                  Card creation in progress
                </p>
                <Button
                  onClick={() => openSheet("pendingCards", 2, {}, false)}
                  className="text-primary-500 hover:text-primary-500 hover:bg-primary-100-hover bg-primary-100 mt-2 w-full py-2 lg:h-9"
                >
                  See Details
                </Button>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2.5 lg:hidden">
            <div className="min-w-1/2 rounded-2xl bg-[linear-gradient(104.01deg,#1F2021_2.88%,#3A3B40_99.58%)] p-4">
              <div className="flex items-center gap-4 text-white">
                <div className="flex items-center gap-2">
                  <img
                    className="size-5"
                    src="/icons/pending-card.svg"
                    alt="Report icon"
                  />
                  <p className="text-xs font-medium text-[#A8CAFF]">
                    Pending card
                  </p>
                </div>
                <span className="text-xs font-extrabold">
                  {pendingCards?.data?.length}
                </span>
              </div>
              <p className="mt-2.5 text-[8px] font-light text-white">
                Card creation in progress
              </p>
            </div>
            <div className="font-dm-sans relative flex h-full min-w-1/2 items-center justify-center space-x-1.5 overflow-hidden rounded-2xl bg-[linear-gradient(104.01deg,#1F2021_2.88%,#3A3B40_99.58%)] p-4 *:grow">
              <DottedBorderBox strokeColor={"#7A766B"} strokeWidth={0.5} />
              <Button
                onClick={() => openSheet("pendingCards", 2, {}, false)}
                className="text-primary-500 hover:text-primary-500 hover:bg-primary-100-hover w-full bg-[#D7F4FF] py-2 lg:h-9"
              >
                See Details
              </Button>
            </div>
          </div>
        </div>
        <div className="bg-bg-primary/90 mt-6 rounded-xl text-white backdrop-blur-xl">
          <div className="flex h-12 items-center justify-between px-4">
            <h1>Recent Transactions</h1>
            <Link
              className="text-primary-100 text-xs hover:underline"
              to="/dashboard/transactions"
            >
              View all
            </Link>
          </div>
          <div className="px-4 backdrop-blur-xl lg:block">
            <div className="hidden lg:block">
              <DataTable
                columns={cardColumns}
                data={cardTransactions?.data.slice(0, 10) ?? []}
                isLoading={isCardLoading}
              />
            </div>
            <div className="lg:hidden">
              <TransactionList
                isLoading={isCardLoading}
                transactions={cardTransactions?.data.slice(0, 10) ?? []}
                type="cards"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cards;
